import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTicketAlt, 
  faReply, 
  faPaperclip, 
  faSpinner, 
  faArrowLeft, 
  faExclamationTriangle,
  faUser,
  faUserShield,
  faUpload,
  faFile
} from '@fortawesome/free-solid-svg-icons';
import './ticketDetails.css'; // Import CSS file (you'll create this)

interface Attachment {
  id: number;
  file_url: string;
  created_at: string;
}

interface Response {
  id: number;
  message: string;
  user_id: number;
  user_name: string;
  user_role: string;
  created_at: string;
  attachments: Attachment[];
}

interface Ticket {
  id: number;
  ticket_number: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  user_id: number;
  created_by_name: string;
  created_by_email: string;
  created_at: string;
  updated_at: string;
  attachments: Attachment[];
  responses: Response[];
}

const TicketDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [responseText, setResponseText] = useState('');
  const [responseFiles, setResponseFiles] = useState<FileList | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [responseError, setResponseError] = useState<string | null>(null);
  
  const [, setStatusUpdate] = useState<string>('');
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  
  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const res = await axios.get(`/api/tickets/${id}`);
        
        // Create a complete ticket object with responses and attachments
        const ticketData = {
          ...res.data.ticket,
          created_by_name: res.data.ticket.user_name || '',
          created_by_email: res.data.ticket.user_email || '',
          responses: res.data.responses || [],
          attachments: res.data.attachments || []
        };
        
        setTicket(ticketData);
        setLoading(false);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Error fetching ticket details');
        setLoading(false);
      }
    };
    
    fetchTicket();
  }, [id]);
  
  const handleResponseChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setResponseText(e.target.value);
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setResponseFiles(e.target.files);
    }
  };
  
  const handleSubmitResponse = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!responseText.trim()) {
      setResponseError('Please enter a response message');
      return;
    }
    
    setSubmitting(true);
    setResponseError(null);
    
    try {
      const responseData = new FormData();
      responseData.append('message', responseText);
      
      if (responseFiles) {
        for (let i = 0; i < responseFiles.length; i++) {
          responseData.append('attachments', responseFiles[i]);
        }
      }
      
      // Submit the response
      const submitResponse = await axios.post(`/api/tickets/${id}/responses`, responseData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Response submitted:', submitResponse.data);
      
      // Update the ticket with the new response
      const updatedTicket = await axios.get(`/api/tickets/${id}`);
      console.log('Updated ticket data:', updatedTicket.data);
      
      // Format the ticket data properly
      const ticketData = {
        ...updatedTicket.data.ticket,
        created_by_name: updatedTicket.data.ticket.user_name || '',
        created_by_email: updatedTicket.data.ticket.user_email || '',
        responses: updatedTicket.data.responses || [],
        attachments: updatedTicket.data.attachments || []
      };
      
      console.log('Formatted ticket data:', ticketData);
      setTicket(ticketData);
      
      // Clear the form
      setResponseText('');
      setResponseFiles(null);
      
      // Reset file input
      const fileInput = document.getElementById('response-attachments') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err: any) {
      console.error('Error submitting response:', err);
      setResponseError(err.response?.data?.message || 'Error submitting response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleStatusChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    if (!newStatus || newStatus === ticket?.status) return;
    
    setStatusUpdate(newStatus);
    setStatusUpdateLoading(true);
    
    try {
      await axios.patch(`/api/tickets/${id}/status`, { status: newStatus });
      
      // Refresh ticket data
      const updatedTicket = await axios.get(`/api/tickets/${id}`);
      
      // Format the ticket data properly
      const ticketData = {
        ...updatedTicket.data.ticket,
        created_by_name: updatedTicket.data.ticket.user_name || '',
        created_by_email: updatedTicket.data.ticket.user_email || '',
        responses: updatedTicket.data.responses || [],
        attachments: updatedTicket.data.attachments || []
      };
      
      setTicket(ticketData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating ticket status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };
  
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-danger';
      case 'in-progress':
        return 'bg-warning';
      case 'resolved':
        return 'bg-success';
      case 'closed':
        return 'bg-secondary';
      default:
        return 'bg-primary';
    }
  };
  
  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-danger';
      case 'medium':
        return 'bg-warning';
      case 'low':
        return 'bg-info';
      default:
        return 'bg-secondary';
    }
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  if (loading) {
    return (
      <div className="text-center py-5">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
        <h3>Loading ticket details...</h3>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        {error}
      </div>
    );
  }
  
  if (!ticket) {
    return (
      <div className="alert alert-warning" role="alert">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        Ticket not found
      </div>
    );
  }
  
  return (
    <div className="container-fluid px-0">
      <div className="row">
        <div className="col-12 text-start">
          <div className="mb-4">
            <button 
              className="btn btn-outline-secondary mb-2"
              onClick={() => navigate(-1)}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back
            </button>
            <h2 className="text-start">
              <FontAwesomeIcon icon={faTicketAlt} className="me-2" />
              Ticket: #{ticket.ticket_number || ticket.id}: {ticket.subject}
            </h2>

            {isAdmin && (
              <div className="d-flex align-items-center mt-2">
                <label htmlFor="status" className="me-2">Status:</label>
                <select
                  id="status"
                  className="form-select"
                  value={ticket.status}
                  onChange={handleStatusChange}
                  disabled={statusUpdateLoading}
                >
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
                {statusUpdateLoading && (
                  <div className="spinner-border spinner-border-sm ms-2" role="status"></div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="card shadow-sm mb-4">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <span className={`badge ${getStatusBadgeClass(ticket.status)} me-2`}>
                {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
              </span>
              <span className={`badge ${getPriorityBadgeClass(ticket.priority)}`}>
                Priority: {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
              </span>
            </div>
            <div className="text-muted small">
              Created: {formatDate(ticket.created_at)}
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="d-flex align-items-center mb-3">
            <div className="bg-primary text-white rounded-circle p-2 me-3">
              <FontAwesomeIcon icon={faUser} />
            </div>
            <div className="text-start">
              <div className="fw-bold">{ticket.created_by_name}</div>
              <div className="text-muted small">{ticket.created_by_email}</div>
            </div>
          </div>
          
          <div className="mb-4">
            <h5>Description</h5>
            <p className="text-start" style={{ whiteSpace: 'pre-line' }}>{ticket.description}</p>
          </div>
          
          {ticket.attachments && ticket.attachments.length > 0 && (
            <div className="mb-3 text-start">
              <h6 className="text-start">
                <FontAwesomeIcon icon={faPaperclip} className="me-2" />
                Attachments ({ticket.attachments.length})
              </h6>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {ticket.attachments.map((attachment) => (
                  <a 
                    key={attachment.id} 
                    href={attachment.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary"
                  >
                    <FontAwesomeIcon icon={faPaperclip} className="me-1" />
                    View Attachment
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Responses Section */}
      <h4 className="mb-3">Responses ({ticket.responses ? ticket.responses.length : 0})</h4>
      
      {ticket.responses && ticket.responses.length > 0 ? (
        <div className="responses-container">
          {ticket.responses.map((response) => (
            <div key={response.id} className="card shadow-sm mb-3">
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <div className={`${response.user_role === 'admin' ? 'bg-primary' : 'bg-secondary'} text-white rounded-circle p-2 me-3`}>
                    <FontAwesomeIcon icon={response.user_role === 'admin' ? faUserShield : faUser} />
                  </div>
                  <div className="text-start">
                    <div className="fw-bold">
                      {response.user_name}
                      {response.user_role === 'admin' && (
                        <span className="badge bg-primary ms-2">Support</span>
                      )}
                    </div>
                    <div className="text-muted small">{formatDate(response.created_at)}</div>
                  </div>
                </div>
                
                <p className="text-start" style={{ whiteSpace: 'pre-line' }}>{response.message}</p>
                
                {response.attachments && response.attachments.length > 0 && (
                  <div className="mt-3 text-start">
                    <h6 className="text-start">
                      <FontAwesomeIcon icon={faPaperclip} className="me-2" />
                      Attachments ({response.attachments.length})
                    </h6>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {response.attachments.map((attachment) => (
                        <a 
                          key={attachment.id} 
                          href={attachment.file_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-secondary"
                        >
                          <FontAwesomeIcon icon={faPaperclip} className="me-1" />
                          View Attachment
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center my-2 py-1">
          <p className="fs-5 fw-semibold text-primary mb-1">No responses yet.</p>
          <p className="text-secondary">Be the first to respond.</p>
        </div>
      )}
      
      {/* Reply Form */}
      {ticket.status !== 'closed' && (
        <div className="card shadow-sm mb-4">
          <div className="card-header">
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faReply} className="me-2" />
              Add Response
            </h5>
          </div>
          <div className="card-body">
            {responseError && (
              <div className="alert alert-danger" role="alert">
                {responseError}
              </div>
            )}
            
            <form onSubmit={handleSubmitResponse}>
              <div className="mb-3">
                <label htmlFor="response-message" className="form-label">Your Message</label>
                <textarea
                  className="form-control"
                  id="response-message"
                  rows={4}
                  value={responseText}
                  onChange={handleResponseChange}
                  placeholder="Type your response here..."
                  required
                ></textarea>
              </div>
              
              <div className="mb-3">
                <label htmlFor="response-attachments" className="form-label fw-bold">
                  <FontAwesomeIcon icon={faPaperclip} className="me-2" />
                  Attachments (Max 5 files)
                  {responseFiles && responseFiles.length > 0 && (
                    <span className="badge bg-primary ms-2">{responseFiles.length} file(s)</span>
                  )}
                </label>
                
                <div className="row g-2 align-items-center">
                  <div className="col-auto">
                    <div className="file-upload-wrapper">
                      <input
                        type="file"
                        className="file-upload-input"
                        id="response-attachments"
                        onChange={handleFileChange}
                        multiple
                        max={5}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="response-attachments" className="file-upload-button btn btn-outline-primary d-flex align-items-center justify-content-center">
                        <FontAwesomeIcon icon={faUpload} className="me-1" />
                        <span>Choose Files</span>
                      </label>
                    </div>
                  </div>
                  
                  <div className="col text-start">
                    <div className="support-text fw-medium">
                      <FontAwesomeIcon icon={faPaperclip} className="me-1" />
                      Supported: Images, PDFs, documents (max 5MB)
                    </div>
                  </div>
                </div>
                
                {responseFiles && responseFiles.length > 0 && (
                  <div className="selected-files mt-3">
                    <h6 className="mb-2 fs-6">Selected Files:</h6>
                    <div className="row g-2">
                      {Array.from(responseFiles).map((file, index) => (
                        <div key={index} className="col-md-6 col-lg-4">
                          <div className="selected-file-item">
                            <FontAwesomeIcon icon={faFile} className="file-icon me-2" />
                            <span className="file-name">{file.name}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="d-grid">
                <button 
                  type="submit" 
                  className="btn btn-primary col-sm-6 col-md-4 col-lg-3 mx-auto d-block"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faReply} className="me-2" />
                      Submit Response
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {ticket.status === 'closed' && (
        <div className="alert alert-warning">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          This ticket is closed. You cannot add new responses.
        </div>
      )}
    </div>
  );
};

export default TicketDetails; 